import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  type OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { NgpSelectPortal } from 'ng-primitives/select';
import {
  heroArrowLeft,
  heroArrowPath,
  heroArrowRight,
  heroBolt,
  heroCheck,
  heroChevronRight,
  heroChevronDown,
  heroClock,
  heroCog6Tooth,
  heroCpuChip,
  heroDocumentText,
  heroMagnifyingGlass,
  heroPlus,
  heroRocketLaunch,
  heroUserCircle,
  heroWrenchScrewdriver,
} from '@ng-icons/heroicons/outline';
import type { AgentArchetype } from '../../../core/api/generated/models/AgentArchetype';
import type { AgentChannelConfigChannelTypeEnumKey } from '../../../core/api/generated/models/AgentChannelConfig';
import type { AgentSkill } from '../../../core/api/generated/models/AgentSkill';
import type { AgentToolDefinition } from '../../../core/api/generated/models/AgentToolDefinition';
import type { Integration } from '../../../core/api/generated/models/Integration';
import type { KnowledgeBase } from '../../../core/api/generated/models/KnowledgeBase';
import {
  UiBadge,
  UiButton,
  UiEmptyText,
  UiEmptyState,
  UiFormField,
  UiInput,
  UiLabel,
  UiNativeSelect,
  UiOptionRow,
  UiOptionRowCheck,
  UiOptionRowCopy,
  UiOptionRowIcon,
  UiPanelCard,
  UiRange,
  UiRecordList,
  UiRecordRow,
  UiSelect,
  UiSelectDropdown,
  UiSelectOption,
  UiRouteTabLink,
  UiRouteTabs,
  UiSectionHeader,
  UiSkeletonRow,
  UiStepNav,
  UiStepPill,
  UiStepPillLabel,
  UiSwitch,
  UiSwitchThumb,
  UiTextarea,
} from '../../../shared/ui';
import { PROVIDER_CATALOG, findProviderBySlug } from '../../integracoes/catalog/provider-catalog';
import {
  ConnectDialogComponent,
  type ConnectDialogDoneEvent,
} from '../../integracoes/components/connect-dialog';
import { ProviderBrandIconComponent } from '../../integracoes/components/provider-brand-icon';
import type { ProviderCatalogEntry } from '../../integracoes/domain/provider-catalog-entry';
import { AgentBuilderStore } from './agent-builder.store';
import type { AgentBuilderMode, AgentBuilderStepId, ChannelBehaviorDraft } from './agent-builder.types';
import { AgentMissionEditorStepComponent } from './agent-mission-editor-step.component';
import { AgentToolConfigPanelComponent } from './agent-tool-config-panel.component';

type DeploymentReviewTone = 'neutral' | 'accent' | 'warning' | 'danger';

type DeploymentReviewItem = {
  label: string;
  value: string;
  detail: string;
  tone: DeploymentReviewTone;
};

type AgentEditTab = 'overview' | 'configuracao' | 'missao' | 'deployments' | 'versoes' | 'execucoes';

type AgentEditTabLink = {
  id: AgentEditTab;
  label: string;
  icon: string;
  path: string | null;
};

type AssistantStage =
  | 'intent'
  | 'identity'
  | 'libraries'
  | 'actions'
  | 'flow'
  | 'operation'
  | 'review';

type AssistantStageDefinition = {
  id: AssistantStage;
  label: string;
};

const ASSISTANT_STAGES: readonly AssistantStageDefinition[] = [
  { id: 'intent', label: 'Briefing' },
  { id: 'identity', label: 'Papel' },
  { id: 'libraries', label: 'Conhecimento' },
  { id: 'actions', label: 'Ações' },
  { id: 'flow', label: 'Conversa' },
  { id: 'operation', label: 'Operação' },
  { id: 'review', label: 'Lançamento' },
];

const CONFIG_STEP_IDS: readonly AgentBuilderStepId[] = ['context', 'persona', 'libraries', 'tools'];

@Component({
  selector: 'app-agent-builder-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIcon,
    NgOptimizedImage,
    NgpSelectPortal,
    RouterLink,
    UiBadge,
    UiButton,
    UiEmptyText,
    UiEmptyState,
    UiFormField,
    UiInput,
    UiLabel,
    UiNativeSelect,
    UiOptionRow,
    UiOptionRowCheck,
    UiOptionRowCopy,
    UiOptionRowIcon,
    UiPanelCard,
    UiRange,
    UiRecordList,
    UiRecordRow,
    UiSelect,
    UiSelectDropdown,
    UiSelectOption,
    UiRouteTabLink,
    UiRouteTabs,
    UiSectionHeader,
    UiSkeletonRow,
    UiStepNav,
    UiStepPill,
    UiStepPillLabel,
    UiSwitch,
    UiSwitchThumb,
    UiTextarea,
    ConnectDialogComponent,
    ProviderBrandIconComponent,
    AgentMissionEditorStepComponent,
    AgentToolConfigPanelComponent,
  ],
  providers: [
    provideIcons({
      heroArrowLeft,
      heroArrowPath,
      heroArrowRight,
      heroBolt,
      heroCheck,
      heroChevronRight,
      heroChevronDown,
      heroClock,
      heroCog6Tooth,
      heroCpuChip,
      heroDocumentText,
      heroMagnifyingGlass,
      heroPlus,
      heroRocketLaunch,
      heroUserCircle,
      heroWrenchScrewdriver,
    }),
  ],
  templateUrl: './agent-builder.page.html',
  styleUrl: './agent-builder.page.css',
})
export class AgentBuilderPage implements OnInit {
  readonly store = inject(AgentBuilderStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly providerCatalog = PROVIDER_CATALOG;
  readonly selectedProviderEntry = signal<ProviderCatalogEntry | null>(null);
  readonly activeEditTab = signal<AgentEditTab>('overview');
  readonly assistantSkippedLibraries = signal(false);
  readonly assistantSkippedActions = signal(false);
  readonly assistantConfirmedLibraries = signal(false);
  readonly assistantConfirmedActions = signal(false);
  readonly assistantConfirmedFlow = signal(false);
  readonly assistantToolConfigId = signal<string | null>(null);
  readonly assistantCurrentStage = signal<AssistantStage>('intent');
  readonly assistantFurthestStageIndex = signal(0);
  readonly assistantStages = ASSISTANT_STAGES;
  readonly assistantStageIndex = computed(() =>
    ASSISTANT_STAGES.findIndex((stage) => stage.id === this.assistantCurrentStage()),
  );
  readonly assistantConfiguredToolIds = computed<readonly string[]>(() => {
    const toolId = this.assistantToolConfigId();
    return toolId && this.store.selectedToolIds().includes(toolId) ? [toolId] : [];
  });
  readonly configStepIds = CONFIG_STEP_IDS;
  readonly editTabs = computed<readonly AgentEditTabLink[]>(() => {
    const agentId = this.store.agentId() ?? this.route.snapshot.paramMap.get('agentId');
    return [
      { id: 'overview', label: 'Visão geral', icon: 'heroCpuChip', path: agentId ? `/agentes/${agentId}` : null },
      { id: 'configuracao', label: 'Configuração', icon: 'heroCog6Tooth', path: agentId ? `/agentes/${agentId}/configuracao` : null },
      { id: 'missao', label: 'Missão', icon: 'heroDocumentText', path: agentId ? `/agentes/${agentId}/missao` : null },
      { id: 'versoes', label: 'Versões', icon: 'heroClock', path: agentId ? `/agentes/${agentId}/versoes` : null },
      { id: 'execucoes', label: 'Execuções', icon: 'heroBolt', path: agentId ? `/agentes/${agentId}/execucoes` : null },
    ];
  });
  readonly messageLengthOptions: readonly {
    value: ChannelBehaviorDraft['messageLength'];
    label: string;
  }[] = [
    { value: 'curta', label: 'Curta' },
    { value: 'normal', label: 'Normal' },
    { value: 'detalhada', label: 'Detalhada' },
  ];
  readonly sensitiveSelectedTools = computed(() =>
    this.store.selectedTools().filter((tool) => isSensitiveToolRisk(tool.riskLevel)),
  );
  readonly acceptedTriggerDrafts = computed(() =>
    this.store.triggerDrafts().filter((draft) => draft.accepted),
  );
  readonly deploymentReviewItems = computed<DeploymentReviewItem[]>(() => {
    const isAutomaticReply = this.store.deploymentMode() === 'automatic_reply';
    const isActive = this.store.deploymentStatus() === 'active';
    const selectedChannels = this.store.selectedAllowedChannels();
    const selectedAccounts = this.store.selectedChannelAccounts();
    const segmentedAccounts = selectedAccounts.filter((account) =>
      Boolean(this.store.deploymentSegmentForAccount(account.id)),
    );
    const sensitiveTools = this.sensitiveSelectedTools();
    const highRiskTools = sensitiveTools.filter((tool) => isHighRiskToolRisk(tool.riskLevel));
    const acceptedTriggerCount = this.acceptedTriggerDrafts().length;

    return [
      {
        label: 'Execução',
        value: this.store.deploymentModeLabel(),
        detail:
          isAutomaticReply && isActive
            ? 'Responde automaticamente nas contas selecionadas.'
            : isActive
              ? 'Apoia o humano sem assumir a conversa sozinho.'
              : 'Não entra em produção enquanto estiver pausado ou desativado.',
        tone: isAutomaticReply && isActive ? 'warning' : isActive ? 'accent' : 'neutral',
      },
      {
        label: 'Escopo',
        value: `${selectedAccounts.length} conta(s)`,
        detail:
          selectedChannels.length === 0
            ? 'Nenhum canal foi escolhido em Contexto.'
            : selectedAccounts.length === 0
              ? 'Nenhuma conta receberá este deployment.'
              : 'Somente essas contas recebem a versão publicada.',
        tone: selectedAccounts.length === 0 ? 'warning' : 'accent',
      },
      {
        label: 'Segmentos',
        value: segmentedAccounts.length ? `${segmentedAccounts.length} restrita(s)` : 'Todos os contatos',
        detail: segmentedAccounts.length
          ? 'Contas segmentadas atendem somente contatos dentro do segmento configurado.'
          : 'Nenhuma conta usa filtro de segmento no atendimento.',
        tone: 'neutral',
      },
      {
        label: 'Tools sensíveis',
        value: sensitiveTools.length ? `${sensitiveTools.length} revisar` : 'Sem alerta',
        detail: sensitiveTools.length
          ? this.sensitiveToolSummary()
          : 'As tools habilitadas não aparecem como alto impacto.',
        tone: highRiskTools.length ? 'danger' : sensitiveTools.length ? 'warning' : 'neutral',
      },
      {
        label: 'Gatilhos',
        value: acceptedTriggerCount ? `${acceptedTriggerCount} aprovado(s)` : 'Nenhum ativo',
        detail: acceptedTriggerCount
          ? 'Gatilhos aprovados entram com as políticas de execução.'
          : 'Sem gatilho aceito, o agente depende dos canais e regras padrão.',
        tone: acceptedTriggerCount ? 'accent' : 'neutral',
      },
      {
        label: 'Versão',
        value: this.store.versionResolutionLabel(),
        detail:
          this.store.versionResolution() === 'latest_published_version'
            ? 'Usa sempre a última versão publicada.'
            : 'Fixa a versão atual do agente para este deployment.',
        tone: 'neutral',
      },
    ];
  });

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const mode = this.route.snapshot.data['agentsMode'] === 'edit' ? 'edit' : 'create';
      const agentId = params.get('agentId') ?? this.route.parent?.snapshot.paramMap.get('agentId') ?? null;
      const rawTab =
        (this.route.snapshot.data['agentTab'] as string | undefined) ??
        params.get('agentTab') ??
        null;
      if (mode === 'create' || this.store.mode() !== mode || this.store.agentId() !== agentId) {
        this.assistantSkippedLibraries.set(false);
        this.assistantSkippedActions.set(false);
        this.assistantConfirmedLibraries.set(false);
        this.assistantConfirmedActions.set(false);
        this.assistantConfirmedFlow.set(false);
        this.assistantToolConfigId.set(null);
        this.assistantCurrentStage.set('intent');
        this.assistantFurthestStageIndex.set(0);
        this.store.load(mode as AgentBuilderMode, agentId);
      }
      this.applyEditTab(rawTab);
    });
  }

  isEditShell(): boolean {
    return this.store.mode() === 'edit';
  }

  isEmbeddedEdit(): boolean {
    return this.route.snapshot.data['agentsEmbedded'] === true;
  }

  async goToEditTab(tab: AgentEditTab): Promise<void> {
    const agentId = this.store.agentId() ?? this.route.snapshot.paramMap.get('agentId') ?? this.route.parent?.snapshot.paramMap.get('agentId');
    if (!agentId) return;
    const path = tab === 'overview' ? ['/agentes', agentId] : ['/agentes', agentId, tab];
    await this.router.navigate(path);
  }

  reloadEditAgent(): void {
    const agentId = this.store.agentId() ?? this.route.snapshot.paramMap.get('agentId') ?? this.route.parent?.snapshot.paramMap.get('agentId');
    if (!agentId) return;
    this.store.load('edit', agentId);
    this.applyEditTab((this.route.snapshot.data['agentTab'] as string | undefined) ?? null);
  }

  isConfigurationTabActive(): boolean {
    return this.activeEditTab() === 'configuracao';
  }

  configStepLabel(stepId: AgentBuilderStepId): string {
    return this.store.steps.find((step) => step.id === stepId)?.label ?? stepId;
  }

  private applyEditTab(rawTab: string | null): void {
    const tab = normalizeEditTab(rawTab);
    this.activeEditTab.set(tab);
    if (tab === 'missao') this.store.goToEditStep('mission');
    if (tab === 'configuracao' && !CONFIG_STEP_IDS.includes(this.store.activeStep())) {
      this.store.goToEditStep('context');
    }
  }

  async back(): Promise<void> {
    await this.router.navigate(['/agentes']);
  }

  openProvider(entry: ProviderCatalogEntry): void {
    if (entry.status !== 'available') return;
    this.selectedProviderEntry.set(entry);
  }

  closeProvider(): void {
    this.selectedProviderEntry.set(null);
  }

  onConnectDone(event: ConnectDialogDoneEvent): void {
    if (event.result.kind === 'success') {
      this.store.refreshIntegrationsAndCapabilities();
    }
  }

  providerEntryForIntegration(provider: string): ProviderCatalogEntry | null {
    return findProviderBySlug(provider as never);
  }

  saveDraft(): void {
    this.store.saveDraft();
  }

  saveDeployments(): void {
    this.store.saveDeploymentConfiguration();
  }

  selectObjective(archetype: AgentArchetype): void {
    const previousArchetype = this.store.selectedArchetype();
    const previousDefaultIntent = previousArchetype ? this.archetypeNorthStar(previousArchetype) : '';
    const shouldRefreshName =
      !this.store.agentName().trim() ||
      Boolean(previousArchetype && this.store.agentName() === `Agente ${previousArchetype.name}`);
    const shouldRefreshIntent =
      !this.store.intent().trim() || Boolean(previousDefaultIntent && this.store.intent() === previousDefaultIntent);

    this.store.selectArchetype(archetype);
    if (!this.isEditShell()) {
      if (shouldRefreshName) this.store.agentName.set(`Agente ${archetype.name}`);
      if (shouldRefreshIntent) this.store.intent.set(this.archetypeNorthStar(archetype));
      this.advanceAssistantStage('identity');
    }
  }

  assistantObjectives(): AgentArchetype[] {
    return this.store.archetypes().slice(0, 8);
  }

  assistantHasStarted(): boolean {
    return Boolean(
      this.store.selectedArchetypeId() ||
        this.store.assistantPrompt().trim() ||
        this.store.intent().trim(),
    );
  }

  assistantStage(): AssistantStage {
    return this.assistantCurrentStage();
  }

  assistantStageLabel(): string {
    return ASSISTANT_STAGES[this.assistantStageIndex()]?.label ?? 'Configuração';
  }

  assistantProgress(): number {
    if (this.store.activeStep() === 'mission') return 100;
    return ((this.assistantStageIndex() + 1) / ASSISTANT_STAGES.length) * 100;
  }

  assistantStageState(stage: AssistantStage): 'active' | 'complete' | 'available' | 'disabled' {
    const index = ASSISTANT_STAGES.findIndex((item) => item.id === stage);
    const currentIndex = this.assistantStageIndex();
    if (index === currentIndex) return 'active';
    if (index < currentIndex) return 'complete';
    if (index <= this.assistantFurthestStageIndex()) return 'available';
    return 'disabled';
  }

  goToAssistantStage(stage: AssistantStage): void {
    const index = ASSISTANT_STAGES.findIndex((item) => item.id === stage);
    if (index < 0 || index > this.assistantFurthestStageIndex()) return;
    this.setAssistantStage(stage);
  }

  confirmAssistantIdentity(): void {
    if (!this.store.agentName().trim() || !this.store.intent().trim()) return;
    this.advanceAssistantStage('libraries');
  }

  confirmAssistantLibraries(): void {
    this.assistantConfirmedLibraries.set(true);
    this.advanceAssistantStage('actions');
  }

  confirmAssistantActions(): void {
    this.assistantConfirmedActions.set(true);
    this.advanceAssistantStage('flow');
  }

  confirmAssistantFlow(): void {
    this.assistantConfirmedFlow.set(true);
    this.advanceAssistantStage('operation');
  }

  confirmAssistantOperation(): void {
    this.advanceAssistantStage('review');
  }

  backAssistantStage(): void {
    const previous = ASSISTANT_STAGES[this.assistantStageIndex() - 1];
    if (previous) this.setAssistantStage(previous.id);
  }

  skipAssistantLibraries(): void {
    this.assistantSkippedLibraries.set(true);
    this.assistantConfirmedLibraries.set(true);
    this.advanceAssistantStage('actions');
  }

  skipAssistantActions(): void {
    this.assistantSkippedActions.set(true);
    this.assistantConfirmedActions.set(true);
    this.advanceAssistantStage('flow');
  }

  assistantDraftStatus(): string {
    const parts: string[] = [];
    if (this.store.selectedArchetypeId()) parts.push(this.store.selectedArchetype()?.name ?? 'Objetivo escolhido');
    if (this.store.selectedKnowledgeBases().length) parts.push(`${this.store.selectedKnowledgeBases().length} biblioteca(s)`);
    if (this.store.selectedTools().length || this.store.selectedSkills().length) {
      parts.push(`${this.store.selectedTools().length + this.store.selectedSkills().length} ação(ões)`);
    }
    return parts.length ? parts.join(' · ') : 'Escolha um caminho ou descreva o agente.';
  }

  assistantKnowledgeBases(): KnowledgeBase[] {
    return this.store.knowledgeBases().slice(0, 8);
  }

  assistantIntegrations(): Integration[] {
    return this.store.integrations().slice(0, 6);
  }

  assistantTools(): AgentToolDefinition[] {
    return this.store.visibleTools();
  }

  selectAssistantTool(tool: AgentToolDefinition): void {
    if (!this.store.selectedToolIds().includes(tool.id)) this.store.toggleTool(tool.id);
    this.assistantToolConfigId.set(tool.id);
  }

  assistantSkills(): AgentSkill[] {
    return this.store.visibleSkills().slice(0, 6);
  }

  applyAssistantPrompt(): void {
    this.store.applyAssistantPrompt();
    if (this.assistantHasStarted()) this.advanceAssistantStage('identity');
  }

  generateAssistedMission(): void {
    this.store.startAssistedCreation();
  }

  returnToAssistantReview(): void {
    this.store.goToEditStep('context');
    this.advanceAssistantStage('review');
  }

  assistantCanGenerate(): boolean {
    return (
      !this.store.saving() &&
      this.store.missionRunStatus() !== 'queued' &&
      this.store.missionRunStatus() !== 'running' &&
      Boolean(
        this.store.agentName().trim() &&
          this.store.intent().trim() &&
          (this.store.assistantPrompt().trim() ||
            this.store.intent().trim() ||
            this.store.selectedArchetypeId()),
      )
    );
  }

  private advanceAssistantStage(stage: AssistantStage): void {
    const index = ASSISTANT_STAGES.findIndex((item) => item.id === stage);
    if (index < 0) return;
    this.assistantFurthestStageIndex.update((current) => Math.max(current, index));
    this.setAssistantStage(stage);
  }

  private setAssistantStage(stage: AssistantStage): void {
    this.assistantCurrentStage.set(stage);
    queueMicrotask(() => {
      this.host.nativeElement.querySelector<HTMLElement>('.builder-canvas')?.scrollTo({ top: 0 });
    });
  }

  archetypeNorthStar(archetype: AgentArchetype): string {
    return (
      readString(archetype.defaultGoalPolicy, 'northStar') || archetype.description || archetype.key
    );
  }

  selectedLibraryLabel(): string {
    const count = this.store.selectedKnowledgeBases().length;
    if (count === 1) return '1 biblioteca selecionada';
    return `${count} bibliotecas selecionadas`;
  }

  selectedToolLabel(): string {
    const count = this.store.selectedTools().length;
    if (count === 1) return '1 ferramenta habilitada';
    return `${count} ferramentas habilitadas`;
  }

  deploymentModeDetail(): string {
    if (this.store.deploymentMode() === 'automatic_reply') {
      return 'O agente pode responder sem revisão humana.';
    }
    return 'O agente sugere respostas para uma pessoa revisar.';
  }

  deploymentStatusDetail(): string {
    if (this.store.deploymentStatus() === 'active') return 'Pode entrar em produção ao publicar.';
    if (this.store.deploymentStatus() === 'paused') return 'Fica configurado, mas não executa.';
    return 'Fica salvo sem disponibilidade operacional.';
  }

  versionResolutionDetail(): string {
    if (this.store.versionResolution() === 'latest_published_version') {
      return 'Acompanha a versão publicada mais recente.';
    }
    return 'Mantém a versão atual neste deployment.';
  }

  deploymentReadinessTitle(): string {
    if (!this.store.missionDocument()) return 'Missão ainda não publicada';
    if (this.store.selectedAllowedChannels().length === 0) return 'Escolha um canal';
    if (this.store.selectedChannelAccounts().length === 0) return 'Selecione uma conta';
    if (this.deploymentReviewItems().some((item) => item.tone === 'danger')) {
      return 'Revisão operacional necessária';
    }
    if (this.deploymentReviewItems().some((item) => item.tone === 'warning')) {
      return 'Pronto com pontos de atenção';
    }
    return 'Pronto para publicar';
  }

  deploymentReadinessDetail(): string {
    if (!this.store.missionDocument()) {
      return 'Gere ou valide a missão antes de ativar deployments.';
    }
    if (this.store.selectedAllowedChannels().length === 0) {
      return 'Volte para Contexto e habilite pelo menos um canal operacional.';
    }
    if (this.store.selectedChannelAccounts().length === 0) {
      return 'A publicação precisa de pelo menos uma conta conectada como destino.';
    }
    if (this.store.deploymentStatus() !== 'active') {
      return 'A configuração será salva, mas não entrará em execução até ser ativada.';
    }
    return 'A versão, o escopo de contas e as políticas abaixo serão aplicados ao publicar.';
  }

  deploymentReviewLabel(): string {
    if (this.deploymentReviewItems().some((item) => item.tone === 'danger')) return 'Revisar risco';
    if (this.deploymentReviewItems().some((item) => item.tone === 'warning')) return 'Atenção';
    return 'Pronto';
  }

  deploymentReviewTone(): DeploymentReviewTone {
    if (this.deploymentReviewItems().some((item) => item.tone === 'danger')) return 'danger';
    if (this.deploymentReviewItems().some((item) => item.tone === 'warning')) return 'warning';
    return 'accent';
  }

  sensitiveToolSummary(): string {
    const tools = this.sensitiveSelectedTools();
    const names = tools.map((tool) => tool.displayName || tool.key).slice(0, 3);
    const suffix = tools.length > names.length ? ` +${tools.length - names.length}` : '';
    return `${names.join(', ')}${suffix}`;
  }

  libraryMeter(base: KnowledgeBase): number {
    return Math.min(100, Math.max(8, (base.activeDocumentCount || 1) * 8));
  }

  selectedAccountCountForChannel(type: AgentChannelConfigChannelTypeEnumKey): number {
    const selected = new Set(this.store.selectedChannelAccountIds());
    return this.store.channelAccountsForType(type).filter((account) => selected.has(account.id))
      .length;
  }

  channelDeploymentSummary(type: AgentChannelConfigChannelTypeEnumKey): string {
    const selected = this.selectedAccountCountForChannel(type);
    const total = this.store.channelAccountCountForType(type);
    const behavior = this.store.channelBehaviors()[type];
    return `${selected}/${total} contas · ${this.store.messageLengthLabel(behavior.messageLength)}`;
  }

  channelPolicySummary(type: AgentChannelConfigChannelTypeEnumKey): string {
    const behavior = this.store.channelBehaviors()[type];
    return `${this.store.messageLengthLabel(behavior.messageLength)} · templates do provider · fallback humano`;
  }

  isSuggestionAccepted(key: string): boolean {
    return this.store.triggerDrafts().some((draft) => draft.id === key && draft.accepted);
  }

  triggerActionLabel(action: string): string {
    const labels: Record<string, string> = {
      handoff: 'Transferência',
      automation: 'Automação',
      system_action: 'Ação do sistema',
      retry: 'Tentar novamente',
      block: 'Bloqueio',
      route: 'Roteamento',
      notify: 'Notificação',
      escalate: 'Escalação',
    };
    return labels[action] ?? action;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readString(source: unknown, key: string): string {
  const value = asRecord(source)[key];
  return typeof value === 'string' ? value : '';
}

function normalizeEditTab(value: string | null): AgentEditTab {
  if (
    value === 'configuracao' ||
    value === 'missao' ||
    value === 'deployments' ||
    value === 'versoes' ||
    value === 'execucoes'
  ) {
    return value;
  }
  return 'overview';
}

function isSensitiveToolRisk(risk: string): boolean {
  return risk === 'medium' || risk === 'high' || risk === 'irreversible';
}

function isHighRiskToolRisk(risk: string): boolean {
  return risk === 'high' || risk === 'irreversible';
}
